import { log } from '@/log'
import ApiBrand from '@/models/api/brand'
import { ApiProduct } from '@/models/api/product'
import {
  defaultAddItemToCartErrorMessage,
  DisplayErrorMessage,
  parseAddItemValidationError,
} from '@/models/cart/error/error-response-parser'
import { isPaidMembershipPrice, SkuPrice } from '@/models/item/sku'
import { CartService } from '@ajax/modules/services/cart'
import { ProductService as AjaxProductService } from '@ajax/modules/services/product'
import { Either, Left, Right } from 'fp-ts/lib/Either'
import { fromNullable } from 'fp-ts/lib/Option'

export interface NewCartItem {
  readonly productId: string,
  readonly count: number,
  readonly price: SkuPrice
}

export interface AddItemToCartResult {
  productId: string
  price: SkuPrice
  count: number
  productNameLabel: string
  productColorLabel: string
  brandEnglishName: string
}

export default class AddItemToCart {
  constructor(
    private cartService: CartService,
    private ajaxProductService: AjaxProductService
  ) {}

  /**
   * @return
   * カート追加に失敗した場合にはエラーメッセージとして{@link string}を返す。
   */
  async execute(items: NewCartItem[]): Promise<Either<DisplayErrorMessage, AddItemToCartResult[]>>
  async execute(items: NewCartItem): Promise<Either<DisplayErrorMessage, AddItemToCartResult>>
  async execute(
    items: NewCartItem | NewCartItem[]
  ): Promise<Either<DisplayErrorMessage, AddItemToCartResult | AddItemToCartResult[]>> {
    if (!Array.isArray(items)) {
      return (await this.execute([items])).map(r => r[0])
    }

    /**
     * FIXME:
     * カート追加結果のオブジェクトの構築に必要
     * 現時点 (2025/09) での用途は以下の通り
     * - 商品が紐づくブランド名はカート遷移に必要
     * - 商品名、カラーはdataLayerにカート追加イベントを落とすために必要
     *
     * 一方でこの情報の取得は必ずしも個々になければならないわけではないので整理したい
     * 候補は以下
     * - プレゼンテーション層に持って行く
     * - カート追加APIがレスポンスに含めるようにして、ここでは結果をそのまま受け渡すだけ
     */
    const result = await this.ajaxProductService.commonItemList({
      with: 'indexsummary,whole',
      ids: items.map(i => i.productId).join(','),
    })
    if (result.isLeft()) {
      return new Left(defaultAddItemToCartErrorMessage)
    }

    const products = result.value
    if (!allProductsBelongingToBrand(products)) {
      return new Left(defaultAddItemToCartErrorMessage)
    }

    const postResult = await this.cartService.post(items.map(i => ({
      productId: i.productId,
      unitPrice: i.price.prices.selected.price,
      isPaidMembershipPrice: isPaidMembershipPrice(i.price),
      unitCount: i.count,
    })))

    if (postResult.isLeft()) {
      const response = (postResult.value as any).response
      const displayErrorMessage = response
        ? parseAddItemValidationError(response)
        : 'カートに追加できませんでした。'

      log.error({ response, displayErrorMessage })

      return new Left(displayErrorMessage)
    }

    const addItemToCartResults = items.map((item) => {
      const maybeProduct = fromNullable(products.find(p => p.id === item.productId))
      const productNameLabel = maybeProduct.map(p => p.fkuProductName || '').getOrElse('')
      const productColorLabel = maybeProduct.map(p => p.colorLabel || '').getOrElse('')
      const brandEnglishName = maybeProduct.map(
        p => p.belongingBrand.englishName
      ).getOrElse('')

      return {
        ...item,
        productNameLabel,
        productColorLabel,
        brandEnglishName,
      }
    })

    log.debug({ addItemToCartResults })

    return new Right(addItemToCartResults)
  }
}

type ProductWithBrand = ApiProduct & {
  belongingBrand: ApiBrand
}
function allProductsBelongingToBrand(products: ApiProduct[]): products is ProductWithBrand[] {
  return products.every(p => p.belongingBrand !== null)
}
